// [350] LO QUE LA PERSONA LEE CUANDO UN GUARD DE LA BASE SE NIEGA.
//
// La mig 350 puso dos triggers que impiden destruir inscripciones pagadas sin
// pasar por la función que paga. Funcionan, y el texto del `RAISE` llega entero
// al navegador —medido: PostgREST lo pone verbatim en `error.message`—. El
// problema es QUÉ dice ese texto: nombra `admin_cancel_session`, que es una
// función de SQL, y la persona que gestiona grupos no tiene una función, tiene
// una pantalla. **Un guard que se niega sin decir dónde se acaba rodeando**: lo
// siguiente que pasa es que alguien pide que se borre a mano por SQL.
//
// ⚠️ POR QUÉ ESTO ES UN MÓDULO Y NO UN `if` EN UNA PÁGINA. Los triggers no son
// de `GruposPage`: saltan en el borrado, y al borrado se llega desde dos sitios
// del panel, contados del grafo y no de memoria:
//
//   · `GruposPage.deleteGroup`      → borra `enrollments` primero  → token 1
//   · `SesionesPage.deleteSession`  → borra la `sessions` directa  → token 2
//
// (Y de ningún otro: ninguna de las 64 RPC que llama el panel borra esas tres
// tablas, y la app no borra ninguna.)
//
// ⚠️ EL TOKEN ES EL CONTRATO, y está atado por los dos lados:
//   · aquí, nombrando la migración que lo emite;
//   · y en `supabase/tests/test_350_una_inscripcion_pagada_no_se_borra.sql`,
//     que AFIRMA los dos tokens literales. Si alguien cambia la frase del
//     `RAISE` en una migración nueva, ese test se pone rojo y su comentario
//     manda aquí.
//
// Un `COMMENT ON FUNCTION` habría sido el lazo débil: medido, el comentario
// NO sale en `pg_get_functiondef`, y la regla del repo es partir siempre de la
// definición viva — así que quien reescriba la función no lo vería.

/** Lo emite `enrollments_lo_que_se_debe_no_se_borra` (mig 350). */
export const TOKEN_INSCRIPCION = 'INSCRIPCION_SIN_LIQUIDAR';

/** Lo emite `sessions_lo_que_se_debe_no_se_borra` (mig 350). */
export const TOKEN_SESION = 'SESION_CON_INSCRIPCIONES_SIN_LIQUIDAR';

// El camino de salida, escrito una vez: es el mismo botón en los dos casos, y
// es el que llama a `admin_cancel_session`. Existe para cualquier sesión no
// cancelada, no solo para las clases especiales (medido en `SesionesPage`).
const COMO_SE_CANCELA =
  'Cancélala en Sesiones: abre la sesión y pulsa «Cancelar sesión». Eso devuelve ' +
  'el bono o da una sesión gratis y avisa a cada familia.';

/**
 * Traduce el error de un borrado a algo que la persona pueda usar.
 *
 * Devuelve `null` si el error NO es de estos guards: entonces el llamante
 * enseña lo que ya enseñaba. Traducir de más sería tapar un error distinto
 * con un texto que no le corresponde.
 *
 * ⚠️ El texto que devuelve SUSTITUYE al mensaje entero, no se interpola dentro
 * del prefijo del llamante. Si se metiera dentro, la persona seguiría leyendo
 * primero «No se pudieron borrar las inscripciones», que es un paso que no ha
 * pedido: ella pulsó «Eliminar definitivamente».
 */
export function mensajeDelGuard(error: { message?: string | null } | null): string | null {
  const msg = error?.message ?? '';
  if (!msg) return null;

  if (msg.includes(TOKEN_INSCRIPCION)) {
    return (
      'Este grupo tiene sesiones con inscripciones pagadas, así que a esas familias ' +
      `se les debe esa sesión. ${COMO_SE_CANCELA} Cuando no quede ninguna sesión con ` +
      'inscripciones vivas, el grupo se podrá eliminar.'
    );
  }

  if (msg.includes(TOKEN_SESION)) {
    return (
      'Esta sesión tiene inscripciones pagadas, así que a esas familias se les debe. ' +
      `No la elimines: ${COMO_SE_CANCELA} Una sesión ya cancelada sí se puede eliminar.`
    );
  }

  return null;
}
